import type { HealthDetailPageId } from './healthDetailPageConfig';

/** HI Havforskningen 2022-14 — Laksvel welfare protocol sections (§7), Norwegian. */
const HI_LAKSVEL_REPORT_NB =
    'https://www.hi.no/hi/nettrapporter/rapport-fra-havforskningen-2022-14';

/** HI Havforskningen 2025-40 — Laksvel welfare protocol, English (EN/ES portal). */
export const HI_LAKSVEL_REPORT_EN =
    'https://www.hi.no/en/hi/nettrapporter/rapport-fra-havforskningen-en-2025-40';

/** Section anchors (kap. 7) — shared across NO and EN publications. */
const LAKSVEL_SECTION_ANCHORS = {
    overview: 'sec-7',
    spine: 'sec-7-2',
    emaciation: 'sec-7-3',
    maturation: 'sec-7-4',
    scaleLoss: 'sec-7-5',
    skinBleeding: 'sec-7-6',
    bodyWounds: 'sec-7-7',
    snoutWounds: 'sec-7-8',
    jaw: 'sec-7-9',
    cataract: 'sec-7-10',
    eyeDamage: 'sec-7-11',
    gillLid: 'sec-7-12',
    finStatus: 'sec-7-14',
} as const;

function hiSection(base: string, anchor: string): string {
    return `${base}#${anchor}`;
}

function usesEnglishProtocolLink(language: string): boolean {
    const normalized = language.toLowerCase().split('-')[0];
    return normalized !== 'nb';
}

const HEALTH_PROTOCOL_HREF_NB: Record<HealthDetailPageId, string> = {
    spine: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.spine),
    emaciation: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.emaciation),
    maturation: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.maturation),
    scaleLoss: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.scaleLoss),
    skinBleeding: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.skinBleeding),
    bodyWounds: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.bodyWounds),
    snoutWounds: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.snoutWounds),
    jaw: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.jaw),
    cataract: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.cataract),
    eyeDamage: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.eyeDamage),
    gillLid: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.gillLid),
    finStatus: hiSection(HI_LAKSVEL_REPORT_NB, LAKSVEL_SECTION_ANCHORS.finStatus),
};

const HEALTH_PROTOCOL_HREF_EN: Record<HealthDetailPageId, string> = {
    spine: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.spine),
    emaciation: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.emaciation),
    maturation: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.maturation),
    scaleLoss: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.scaleLoss),
    skinBleeding: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.skinBleeding),
    bodyWounds: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.bodyWounds),
    snoutWounds: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.snoutWounds),
    jaw: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.jaw),
    cataract: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.cataract),
    eyeDamage: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.eyeDamage),
    gillLid: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.gillLid),
    finStatus: hiSection(HI_LAKSVEL_REPORT_EN, LAKSVEL_SECTION_ANCHORS.finStatus),
};

export const HEALTH_LAKSVEL_OVERVIEW_PROTOCOL_HREF_NB = hiSection(
    HI_LAKSVEL_REPORT_NB,
    LAKSVEL_SECTION_ANCHORS.overview
);

export const HEALTH_LAKSVEL_OVERVIEW_PROTOCOL_HREF_EN = hiSection(
    HI_LAKSVEL_REPORT_EN,
    LAKSVEL_SECTION_ANCHORS.overview
);

export function getHealthLaksvelOverviewProtocolHref(language: string): string {
    return usesEnglishProtocolLink(language)
        ? HEALTH_LAKSVEL_OVERVIEW_PROTOCOL_HREF_EN
        : HEALTH_LAKSVEL_OVERVIEW_PROTOCOL_HREF_NB;
}

export function getHealthProtocolHref(pageId: HealthDetailPageId, language: string): string {
    return usesEnglishProtocolLink(language)
        ? HEALTH_PROTOCOL_HREF_EN[pageId]
        : HEALTH_PROTOCOL_HREF_NB[pageId];
}
